import React, { useCallback, useRef, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import {
  getVendorProfileMediaRule,
  readImageDimensions,
  validateExactVendorProfileMedia,
  validateImageFile,
} from "../utilities/imageUpload";

/**
 * Crop a vendor's profile picture (logo, card image, shop banner) to the exact
 * size its role requires.
 * ─────────────────────────────────────────────────────────────────────────────
 * Every vendor profile image has ONE exact size (utilities/imageUpload.js
 * VENDOR_PROFILE_MEDIA_RULES, mirrored by the server and the vendor artwork
 * guide): a shop logo is 588x558, a card image 1280x720, and so on. Until
 * 1 Nov 2026 a file of any other size was simply refused, so the only way to
 * replace a logo was to resize it in another program first.
 *
 * Now a file that is already exact goes straight through; any other image
 * opens a dialog with the crop frame locked to the role's shape, and the
 * chosen area is saved at exactly the role's size. The exact-size rule and
 * every other check (type, byte limit) are unchanged: the result goes through
 * validateExactVendorProfileMedia like any upload.
 *
 * Usage, once per form:
 *   const { fitVendorMedia, mediaCropDialog } = useVendorMediaCrop();
 *   file = await fitVendorMedia(file, "retailer_wholesaler", "logo");
 *   if (!file) return;               // the user cancelled the crop
 *   ...render {mediaCropDialog} anywhere in the form.
 */

const ACTION = "#083860";

/* Quality steps tried when the exported image is over the role's byte limit. */
const JPEG_QUALITIES = [0.92, 0.85, 0.75, 0.65];

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve) => canvas.toBlob(resolve, type, quality));

/* Draws the crop (in the displayed image's pixels) at exactly width x height. */
const renderCrop = (image, pixelCrop, width, height, background) => {
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  if (background) {
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, width, height);
  }
  ctx.drawImage(
    image,
    pixelCrop.x * scaleX,
    pixelCrop.y * scaleY,
    pixelCrop.width * scaleX,
    pixelCrop.height * scaleY,
    0,
    0,
    width,
    height,
  );
  return canvas;
};

/* PNG and WebP keep their format (a logo may have transparency); anything
   else is JPEG. If the result is over the role's limit, fall back to JPEG at
   falling quality on a white ground. */
const exportCrop = async (image, pixelCrop, rule, sourceFile) => {
  const keepType =
    sourceFile.type === "image/png" || sourceFile.type === "image/webp"
      ? sourceFile.type
      : null;
  if (keepType) {
    const blob = await canvasToBlob(
      renderCrop(image, pixelCrop, rule.width, rule.height, null),
      keepType,
    );
    if (blob && blob.size <= rule.maxBytes) return blob;
  }
  const flat = renderCrop(image, pixelCrop, rule.width, rule.height, "#ffffff");
  for (const quality of JPEG_QUALITIES) {
    const blob = await canvasToBlob(flat, "image/jpeg", quality);
    if (blob && blob.size <= rule.maxBytes) return blob;
  }
  return canvasToBlob(flat, "image/jpeg", JPEG_QUALITIES[JPEG_QUALITIES.length - 1]);
};

const fileNameFor = (sourceFile, rule, type) => {
  const base = String(sourceFile.name || "image").replace(/\.[^.]+$/, "");
  const ext = type === "image/png" ? "png" : type === "image/webp" ? "webp" : "jpg";
  return `${base}-${rule.width}x${rule.height}.${ext}`;
};

export const useVendorMediaCrop = () => {
  // { src, file, rule, natural, resolve, reject } while the dialog is open
  const [job, setJob] = useState(null);
  const [crop, setCrop] = useState();
  const [pixelCrop, setPixelCrop] = useState(null);
  const [saving, setSaving] = useState(false);
  const imageRef = useRef(null);

  /* Ends the dialog: a File, null (cancelled) or an Error, which the form's
     own catch turns into a toast. */
  const close = useCallback(
    (result) => {
      if (job) {
        URL.revokeObjectURL(job.src);
        if (result instanceof Error) job.reject(result);
        else job.resolve(result);
      }
      setJob(null);
      setCrop(undefined);
      setPixelCrop(null);
      setSaving(false);
    },
    [job],
  );

  /**
   * Resolves to a File of exactly the role's size, or null when the user
   * cancels. Throws (with a message for the toast) when the file is not a
   * usable image or the result breaks the role's rules.
   */
  const fitVendorMedia = useCallback(async (file, vendorType, mediaRole) => {
    const rule = getVendorProfileMediaRule(vendorType, mediaRole);
    if (!rule) throw new Error("This vendor media role is not configured.");
    const basic = validateImageFile(file);
    if (!basic.valid) throw new Error(basic.error);

    const { width, height } = await readImageDimensions(file);
    if (width === rule.width && height === rule.height) {
      await validateExactVendorProfileMedia(file, vendorType, mediaRole);
      return file;
    }

    const cropped = await new Promise((resolve, reject) => {
      setJob({ src: URL.createObjectURL(file), file, rule, natural: { width, height }, resolve, reject });
    });
    if (!cropped) return null;
    await validateExactVendorProfileMedia(cropped, vendorType, mediaRole);
    return cropped;
  }, []);

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    const aspect = job.rule.width / job.rule.height;
    // Start with the largest frame of the right shape, centred.
    const initial = centerCrop(
      makeAspectCrop({ unit: "%", width: 100 }, aspect, width, height),
      width,
      height,
    );
    setCrop(initial);
    setPixelCrop({
      unit: "px",
      x: (initial.x / 100) * width,
      y: (initial.y / 100) * height,
      width: (initial.width / 100) * width,
      height: (initial.height / 100) * height,
    });
  };

  const applyCrop = async () => {
    if (!job || !imageRef.current || !pixelCrop?.width || !pixelCrop?.height) return;
    setSaving(true);
    try {
      const blob = await exportCrop(imageRef.current, pixelCrop, job.rule, job.file);
      if (!blob) throw new Error("The cropped image could not be created.");
      close(new File([blob], fileNameFor(job.file, job.rule, blob.type), { type: blob.type }));
    } catch (err) {
      close(err instanceof Error ? err : new Error("The cropped image could not be created."));
    }
  };

  // How many source pixels the frame covers, to warn before enlarging.
  const sourceArea =
    job && imageRef.current && pixelCrop?.width
      ? {
          width: Math.round(pixelCrop.width * (imageRef.current.naturalWidth / imageRef.current.width)),
          height: Math.round(pixelCrop.height * (imageRef.current.naturalHeight / imageRef.current.height)),
        }
      : null;
  const enlarges =
    sourceArea && job && (sourceArea.width < job.rule.width || sourceArea.height < job.rule.height);

  const mediaCropDialog = (
    <Modal show={Boolean(job)} onHide={() => close(null)} size="lg" centered backdrop="static">
      {job && (
        <>
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: "1.05rem", fontWeight: 600 }}>
              Crop {job.rule.shortLabel.toLowerCase()}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p style={{ fontSize: "0.85rem", color: "#475569", marginBottom: 12 }}>
              This image is {job.natural.width}x{job.natural.height}px. Drag the frame to choose the
              area to keep; it is saved at exactly {job.rule.width}x{job.rule.height}px.
            </p>
            <div style={{ display: "flex", justifyContent: "center", background: "#f1f5f9", borderRadius: 8, padding: 8 }}>
              <ReactCrop
                crop={crop}
                onChange={(px, percent) => setCrop(percent)}
                onComplete={(px) => setPixelCrop(px)}
                aspect={job.rule.width / job.rule.height}
                keepSelection
              >
                <img
                  ref={imageRef}
                  src={job.src}
                  alt=""
                  onLoad={onImageLoad}
                  style={{ maxHeight: "60vh", maxWidth: "100%", display: "block" }}
                />
              </ReactCrop>
            </div>
            {enlarges && (
              <p style={{ fontSize: "0.8rem", color: "#B42318", margin: "10px 0 0" }}>
                The chosen area is {sourceArea.width}x{sourceArea.height}px, smaller than{" "}
                {job.rule.width}x{job.rule.height}px, so it will be enlarged and may look soft. A
                larger original gives a sharper result.
              </p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              onClick={() => close(null)}
              disabled={saving}
              style={{ background: "#e2e8f0", borderColor: "#e2e8f0", color: "#0f172a" }}
            >
              Cancel
            </Button>
            <Button
              onClick={applyCrop}
              disabled={saving || !pixelCrop?.width}
              style={{ background: ACTION, borderColor: ACTION }}
            >
              {saving ? "Cropping..." : "Use this crop"}
            </Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );

  return { fitVendorMedia, mediaCropDialog };
};

export default useVendorMediaCrop;
